import { ProductType, isKorpoRegisterMode, PRODUCTS, RegisterMode } from 'cart-model';
import { useTransRedirect } from 'common-react';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logSetAmountsPerFundEntry } from '../../model/events';
import ProductWizardState from '../../model/ProductWizardState';
import { productWizardActions } from '../../model/store';
import CartSumBar from '../Cart/CartSumBar/CartSumBar';
import { IkzeExtendedEntrepreneurDepositAgreementForm } from '../IkzeExtendedEntrepreneurDeposit/IkzeExtendedEntrepreneurDepositAgreement';
import { ProductWizardTitle } from '../ProductWizard/ProductWizardTitle/ProductWizardTitle';
import { getDynamicTitleKey } from '../ProductWizard/ProductWizardTitle/getDynamicTitleKey';
import Screen from '../ProductWizard/Screen';
import { validateCart, AmountValidationErrorType } from './cartValidation';
import SetAmountsProductSection from './SetAmountsProductSection/SetAmountsProductSection';
import { useTranslation } from 'react-i18next';
import useStyles from './SetAmountsPerFund.style';

const SetAmountsPerFund: React.FC = () => {
  const [t] = useTranslation('product-wizard');
  useEffect(() => logSetAmountsPerFundEntry(), []);

  const c = useStyles({});

  const dispatch = useDispatch();

  const cartFunds = useSelector((state: ProductWizardState) => state.cartState.cartFunds);
  const currentLimits = useSelector((state: ProductWizardState) => state.apiState.currentLimits);
  const [isIkzeExtendedFormValid, setIkzeExtendedFormValid] = useState(true);
  const [ikzeExtendedFormData, setIkzeExtendedFormData] = useState<IkzeExtendedEntrepreneurDepositAgreementForm>({
    isEntrepreneur: false,
    nip: undefined,
    entrepreneurConsent: undefined,
  });
  const registerMode = useSelector((state: ProductWizardState) => state.cartState.registerMode);
  const korpoData = useSelector((state: ProductWizardState) => state.cartState.korpoData);
  const redirect = useTransRedirect();
  if (cartFunds.length === 0) {
    redirect(Screen.SELECT_FUNDS.code);
    return null;
  }

  const isKorpo = isKorpoRegisterMode(registerMode);

  const handleIkzeExtendedFormChange = (values: IkzeExtendedEntrepreneurDepositAgreementForm, formValid: boolean) => {
    setIkzeExtendedFormValid(formValid);
    setIkzeExtendedFormData(values);
  };

  const setIkzeExtendedFormDataInState = () => {
    if (cartFundsByProductType[ProductType.ikze] != null && cartFundsByProductType[ProductType.ikze].length > 0) {
      dispatch(productWizardActions.setIsEntrepreneur(ikzeExtendedFormData.isEntrepreneur));
      dispatch(productWizardActions.setEntrepreneurNip(ikzeExtendedFormData.isEntrepreneur
          ? ikzeExtendedFormData.nip
          : undefined));
    }
  };

  const productTypes: ProductType[] = Object.values(ProductType);
  const isEntrepreneurLimitEnabled = ikzeExtendedFormData.isEntrepreneur && isIkzeExtendedFormValid;

  const { valid, productTypeToValidationError, cartFundToValidationError } =
      validateCart(cartFunds, currentLimits, isEntrepreneurLimitEnabled, isKorpo && korpoData != null ? korpoData : undefined);

      const pensionSumError = productTypeToValidationError[ProductType.ike].type === AmountValidationErrorType.MAX
    || productTypeToValidationError[ProductType.ikze].type === AmountValidationErrorType.MAX;

  const cartFundsByProductType = _.groupBy(cartFunds, cf => cf.productType);
  const multipleProductTypes = _.values(cartFundsByProductType).length > 1;

  const getTitleKey = (): string => {
    const productTypes = Object.keys(cartFundsByProductType);

    if (productTypes.length === 1) {
      const productType = productTypes[0] as ProductType;
      return getDynamicTitleKey(productType, 'set-amounts');
    }

    return Screen.SET_AMOUNTS_PER_FUND.code;
  };

  const isBasicProduct = (): boolean => {
    const productTypes = Object.keys(cartFundsByProductType);
    return productTypes.length === 1 && productTypes[0] === ProductType.funds;
  }

  const getSubtitleKey = (): string | undefined => {
    const productTypes = Object.keys(cartFundsByProductType);
    if (productTypes.length === 1) {
      const productType = productTypes[0] as ProductType;
        switch (registerMode) {
          case RegisterMode.OPEN_KORPO:
            return `open-${productType.toLowerCase()}-korpo`;
          case RegisterMode.EXT_TRANSFER_KORPO:
            return `transfer-${productType.toLowerCase()}-from-other-to-korpo`;
          case RegisterMode.PPE_TRANSFER_PAYMENT_KORPO:
            return `transfer-${productType.toLowerCase()}-from-ppe`;
          case RegisterMode.TRANSFER:
            return `transfer-${productType.toLowerCase()}-from-other`;
          case RegisterMode.PPE_TRANSFER_PAYMENT:
            return `transfer-${productType.toLowerCase()}-from-ppe`;
          case RegisterMode.OPEN:
            return `open-${productType.toLowerCase()}`;
          default:
            return Screen.SELECT_FUNDS.code;
        }
    }
  };

  return (
    <>
      <ProductWizardTitle
        titleKey={getTitleKey()}
        subtitle={!isBasicProduct() ? t(`title.${getSubtitleKey()}`) : undefined}
      />
      <div className={c.root}>
        {productTypes.map(productType => {
          const unitCategory = isKorpo && korpoData != null ? korpoData.unitCategoryCode : PRODUCTS[productType].unitCategory;
          const korpoFunds = isKorpo && korpoData != null ? korpoData.allowedFunds : undefined;
          return cartFundsByProductType[productType]
            ? <SetAmountsProductSection
                  key={productType}
                  showHeader={multipleProductTypes}
                  productType={productType}
                  unitCategory={unitCategory}
                  cartFunds={cartFundsByProductType[productType]}
                  validationError={productTypeToValidationError[productType]}
                  cartFundToValidationError={cartFundToValidationError}
                  sumError={pensionSumError}
                  handleIkzeExtendedFormChange={handleIkzeExtendedFormChange}
                  korpoFunds={korpoFunds}
              />
            : null;
        })}
        <CartSumBar
          nextEnabled={valid && isIkzeExtendedFormValid}
          onNextClick={() => setIkzeExtendedFormDataInState()}
          productTypeToValidationError={productTypeToValidationError}
          sumError={pensionSumError}
        />
      </div>
    </>
  );
};

export default SetAmountsPerFund;
